/**
 * PreviewWorkerClient: one Worker per page, lazily spawned. `run` resolves with the point; the look-through and the range rows arrive through
 * `onRanges` (each a fuller result than the point). A newer `run`, or `cancel`, supersedes the one in flight (its promise rejects with PreviewSuperseded).
 * A crash rejects the run and drops the Worker, so the next `run` (Retry) starts a fresh one.
 */
import type { DecisionSet, PreviewInput, PreviewResult, PreviewTrace } from "@bsim/domain";
import type { PreviewWatch } from "./watch";
import type { WorkerReply, WorkerRequest } from "./protocol";

export interface PreviewWorkerClient {
  run(input: PreviewInput, draft: DecisionSet, valueLegs?: boolean): Promise<PreviewResult>;
  onRanges(cb: (r: PreviewResult) => void): void;
  onWatch(cb: (w: PreviewWatch) => void): void;
  onTrace(cb: (t: PreviewTrace) => void): void;
  /** One step of the probed decision (see `probe`) as it arrives, for the run in flight. */
  onNextStep(cb: (s: { readonly direction: 1 | -1; readonly decisionId: string; readonly result: PreviewResult }) => void): void;
  /** Names the decision to step one option each way after every run (a setting's leaf path), or none. Never starts a run. */
  probe(decisionId: string | null): void;
  cancel(): void;
}

export class PreviewSuperseded extends Error {
  constructor() { super("Superseded by a newer preview run"); }
}

/** The slice of `Worker` the client uses; tests pass a fake with the same shape. */
export interface WorkerLike {
  onmessage: ((e: MessageEvent<WorkerReply>) => void) | null;
  onerror: ((e: ErrorEvent) => void) | null;
  postMessage(msg: WorkerRequest): void;
  terminate(): void;
}

const spawnModuleWorker = (): WorkerLike => new Worker(new URL("./worker.ts", import.meta.url), { type: "module" });

export function createPreviewWorker(spawn: () => WorkerLike = spawnModuleWorker): PreviewWorkerClient {
  let worker: WorkerLike | null = null;
  let nextId = 0;
  let pending: { readonly resolve: (r: PreviewResult) => void; readonly reject: (e: unknown) => void } | null = null;
  const rangeListeners: ((r: PreviewResult) => void)[] = [];
  const watchListeners: ((w: PreviewWatch) => void)[] = [];
  const traceListeners: ((t: PreviewTrace) => void)[] = [];
  const stepListeners: Parameters<PreviewWorkerClient["onNextStep"]>[0][] = [];

  const settle = (fail: unknown): void => { const p = pending; pending = null; p?.reject(fail); };

  function ensureWorker(): WorkerLike {
    if (worker !== null) return worker;
    const w = spawn();
    w.onmessage = ({ data }) => {
      if (data.id !== nextId) return;
      if (data.kind === "point") { const p = pending; pending = null; p?.resolve(data.result); }
      else if (data.kind === "watch") watchListeners.forEach((cb) => cb(data.watch));
      else if (data.kind === "trace") traceListeners.forEach((cb) => cb(data.trace));
      else if (data.kind === "nextStep") stepListeners.forEach((cb) => cb({ direction: data.direction, decisionId: data.decisionId, result: data.result }));
      else if (data.kind === "ranges" || data.kind === "lookThrough" || data.kind === "scoreRange") rangeListeners.forEach((cb) => cb(data.result));
      else settle(new Error(data.message));
    };
    w.onerror = () => { settle(new Error("Preview worker crashed")); w.terminate(); worker = null; };
    worker = w;
    return w;
  }

  return {
    run(input, draft, valueLegs) {
      settle(new PreviewSuperseded());
      const id = ++nextId;
      return new Promise<PreviewResult>((resolve, reject) => {
        pending = { resolve, reject };
        ensureWorker().postMessage({ kind: "run", id, input, draft, ...(valueLegs === true ? { valueLegs: true as const } : {}) });
      });
    },
    onRanges: (cb) => { rangeListeners.push(cb); },
    onWatch: (cb) => { watchListeners.push(cb); },
    onTrace: (cb) => { traceListeners.push(cb); },
    onNextStep: (cb) => { stepListeners.push(cb); },
    probe(decisionId) { if (decisionId === null && worker === null) return; ensureWorker().postMessage({ kind: "probe", id: nextId, decisionId }); },
    cancel() {
      settle(new PreviewSuperseded());
      worker?.postMessage({ kind: "cancel", id: ++nextId });
    },
  };
}
